import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Music, UserPlus, UserCheck } from "lucide-react";
import { motion } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Avatar } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import VerifiedBadge from "@/components/VerifiedBadge";
import TopEarners from "@/components/TopEarners";
import { toast } from "sonner";

function ArtistCard({ artist, meId, onToggle }) {
  const [following, setFollowing] = useState(artist.is_following);
  const [followers, setFollowers] = useState(artist.followers);
  const isMe = artist.id === meId;

  const toggle = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/artists/${artist.id}/follow`);
      setFollowing(data.is_following); setFollowers(data.followers);
    } catch { toast.error("Action failed"); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="nu-card overflow-hidden group">
      <Link to={`/artist/${artist.id}`}>
        <div className="h-24 relative overflow-hidden">
          {artist.cover_url ? <img src={mediaUrl(artist.cover_url)} alt="" className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
            : <div className="w-full h-full bg-gradient-to-br from-violet-500/30 to-emerald-500/20" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#181824] to-transparent" />
        </div>
        <div className="px-5 pb-5 -mt-8 relative">
          <Avatar artist={artist} size={56} />
          <p className="mt-3 font-bold truncate group-hover:text-emerald-400 transition-colors flex items-center gap-1">{artist.artist_name}<VerifiedBadge show={artist.is_subscribed} size={15} /></p>
          <p className="text-xs text-zinc-500 font-mono-nu truncate">@{artist.handle}</p>
          <p className="text-xs text-zinc-400 mt-1 truncate">{artist.genre || "Music Artist"}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-zinc-400 font-mono-nu">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {followers}</span>
            <span className="flex items-center gap-1"><Music className="w-3 h-3" /> {artist.tracks}</span>
          </div>
        </div>
      </Link>
      {!isMe && (
        <div className="px-5 pb-5">
          <button onClick={toggle} data-testid={`follow-btn-${artist.id}`}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-full text-sm font-bold transition-colors ${
              following ? "bg-white/10 text-white hover:bg-white/20" : "bg-emerald-500 text-black hover:bg-emerald-600"}`}>
            {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
          </button>
        </div>
      )}
    </motion.div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/artists").then(({ data }) => setArtists(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Discover <span className="text-gradient">Artists</span></h1>
      <p className="text-zinc-400 mt-2">Find and follow the rising talent on NEXT UP.</p>
      <div className="mt-6 max-w-2xl"><TopEarners /></div>
      {loading ? <p className="text-zinc-500 text-center py-16 font-mono-nu">loading…</p> : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="artists-grid">
          {artists.map((a) => <ArtistCard key={a.id} artist={a} meId={user.id} />)}
        </div>
      )}
    </div>
  );
}
